/**
 * `ez.a.create_subitem` — create a subitem under the triggering item.
 *
 * A subitem is an item on the parent board's **subitems board** (doc 04's subitem model), which
 * is created lazily by the first subitem `[sub§2]` — so the block cannot name the board and does
 * not try: it calls `services.createSubitem`, which resolves (and creates) it. Calling
 * `createItem` with the parent's own board id is P-202, and `items.createItem` refuses it. No
 * monday `workflowBlockId` for it appears in the captured catalogue for this account's recipes,
 * hence the `ez.*` id.
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
import { interpolateTokens } from '../tokens.js';
import { tokenContextFor } from './token-context.js';
export const createSubitemParams = z.object({
    name: z.string().default('New subitem'),
    columnValues: z.record(z.string(), z.unknown()).default({}),
});
export const createSubitemAction = defineAction({
    id: 'ez.a.create_subitem',
    kind: 'action',
    app: 'monday',
    sentencePartial: 'create a subitem {name, name}',
    paramsSchema: createSubitemParams,
    executable: true,
    assumed: true,
    references: () => [],
    async execute(context, params) {
        if (context.itemId === null)
            return { skipped: 'no_item' };
        const parent = await context.services.getItem(context.itemId);
        if (parent === null)
            return { skipped: 'item_deleted' };
        /*
         * **P-296.** The name is the one string this block interpolates, so it is the one string the
         * token context is built from — without it `{pulse.<columnId>}` in a subitem's name could
         * never resolve. `columnValues` is **not** interpolated here (and so is not pre-resolved
         * either): monday's own behaviour for a subitem's cell values is not captured anywhere, and
         * interpolating them would be a new behaviour, not a fix (open question q581).
         */
        const tokens = await tokenContextFor(context, [params.name]);
        const created = await context.services.createSubitem({
            parentItemId: context.itemId,
            name: interpolateTokens(params.name, tokens),
            columnValues: params.columnValues,
        });
        // `boardId` is the subitems board the service resolved, which is what the run log should
        // show — the parent's board is already on the run row.
        return { itemId: created.id, boardId: created.boardId, parentItemId: context.itemId };
    },
});
//# sourceMappingURL=ez.a.create-subitem.js.map